export enum EVENT_TYPES {
  VIRTUAL = "virtual",
  ON_SITE = "on-site",
  HYBRID = "hybrid",
}

export enum MEETING_PROVIDERS {
  GOOGLE_MEET = "google_meet",
  ZOOM = "zoom",
}

export enum EVENT_SEND_REMINDER_OPTIONS {
  AT_TIME = "at-time",
  BEFORE_15_MIN = "15-min",
  BEFORE_1_HOUR = "1-hour",
  BEFORE_1_DAY = "1-day",
}

export enum EVENT_FREQUENCY_OPTIONS {
  NEVER = "never",
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

// Predefined event languages (single-select dropdown). Must match the backend
// EVENT_LANGUAGES list in API.MAIHRMS events enums.
export const EVENT_LANGUAGES = [
  "English",
  "Arabic",
  "Hindi",
  "Urdu",
  "French",
  "Spanish",
  "Tagalog",
  "Malayalam",
  "Tamil",
  "Bengali",
] as const;

export interface IGenerateEventMeetingLinkPayload {
  title: string;
  timezone: string;
  description: string;
  startDateTime: Date;
  durationMinutes: number;
  provider?: MEETING_PROVIDERS;
}

export interface IGenerateEventMeetingLinkResponse {
  meetingLink: string;
}

export enum EVENT_STATUSES {
  UPCOMING = "upcoming",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ARCHIVED = "archived",
  EXPIRED = "expired",
}

export enum EVENT_INVITATION_STATUSES {
  PENDING = "pending",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  EXPIRED = "expired",
}

export interface ISingleCompanyEvent {
  id: number;
  eventName: string;
  eventType: EVENT_TYPES;
  startDateTime: string;
  endDateTime: string;
  description: string;
  location: string | null;
  meetingLink: string;
  timezone: string;
  twoParticipantsImages: {
    fullName: string;
    profileImage: string | null;
  }[];
  otherParticipantsCount: number;
  status: EVENT_STATUSES;
  branchId: number | null;
  branch: { id: number; name: string; code: string } | null;
}

export interface IEventStats {
  totalEvents: number;
  completedEvents: number;
  cancelledEvents: number;
  archivedEvents: number;
}

export interface EventAttachment {
  id: number;
  eventId: number;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
}

interface SingleEventInvitee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string | null;
  status: string;
  respondedAt: string | null;
  responseNote: string | null;
}

export interface IEventBreak {
  id?: number;
  title: string | null;
  startDateTime: string;
  endDateTime: string;
}

export interface IExternalInvitee {
  id?: number;
  email: string;
  name: string | null;
  status?: EVENT_INVITATION_STATUSES;
  respondedAt?: string | null;
  responseNote?: string | null;
}

export interface IInviteeGroup {
  id: number;
  name: string;
  memberUserIds: number[];
}

export interface IInviteeGroupsResponse {
  departments: IInviteeGroup[];
  teams: IInviteeGroup[];
  projects: IInviteeGroup[];
}

export interface IEventDetails {
  id: number;
  title: string;
  type: EVENT_TYPES;
  startDateTime: string;
  endDateTime: string;
  timezone: string;
  description: string;
  meetingLink: string | null;
  location: string | null;
  branchId: number | null;
  branch: { id: number; name: string; code: string } | null;
  inviteEveryone: boolean;
  recurrenceDays: string[];
  recurrence: EVENT_FREQUENCY_OPTIONS;
  status: EVENT_STATUSES;
  createdBy: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage: string;
  };
  posterUrl: string | null;
  language: string | null;
  breaks: IEventBreak[];
  attachments: EventAttachment[];
  invitedPeople: SingleEventInvitee[];
  externalInvitees: IExternalInvitee[];
}

export interface IEventSingleParticipant {
  userId: number | null;
  name: string;
  email: string;
  status: EVENT_INVITATION_STATUSES;
  profileImage: string | null;
  responseNote: string | null;
  respondedAt: string | null;
  isExternal?: boolean;
}

export interface EventParticipantsData {
  participants: IEventSingleParticipant[];
  stats: {
    total: number;
    accepted: number;
    rejected: number;
    pending: number;
  };
}

export interface Creator {
  id: number;
  name: string;
  email: string;
}

export interface EventParticipant {
  id: number;
  userId: number | null;
  name: string;
  email: string;
  profileImage: string | null;
  department: string | null;
  position: string | null;
  status: EVENT_INVITATION_STATUSES;
  responseNote: string | null;
  respondedAt: string | null;
  invitedAt: string;
  isExternal?: boolean;
}

export interface ParticipantGroups {
  accepted: EventParticipant[];
  rejected: EventParticipant[];
  pending: EventParticipant[];
}

interface IEventReportStats {
  totalInvited: number;
  accepted: number;
  rejected: number;
  pending: number;
}

export interface EventReportResponse {
  event: IEventDetails;
  stats: IEventReportStats;
  participants: ParticipantGroups;
  allParticipants: EventParticipant[];
}

// ─── Public RSVP (external invitees, unauthenticated) ───────────────────────
export interface IPublicRsvpEvent {
  id: number;
  title: string;
  type: EVENT_TYPES;
  description: string;
  startDateTime: string;
  endDateTime: string;
  timezone: string;
  location: string | null;
  meetingLink: string | null;
  language: string | null;
  posterUrl: string | null;
  status: EVENT_STATUSES;
  breaks: IEventBreak[];
  createdBy: { name: string; email: string } | null;
}

export interface IPublicRsvpResponse {
  invitation: {
    email: string;
    name: string | null;
    status: EVENT_INVITATION_STATUSES;
    respondedAt: string | null;
    responseNote: string | null;
  };
  event: IPublicRsvpEvent | null;
}
